import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, Loader2 } from 'lucide-react';
import {
  AssistantMessageRole,
  type AssistantDecision,
  type AssistantMessage,
} from '@xtreed/academy-shared';
import { cn } from '@/lib/utils';
import type { AssistantUploadView } from '@/hooks/assistant/useAssistantUploads';
import { MessageBubble } from './MessageBubble';
import { TypingIndicator } from './TypingIndicator';

const STAGGER_MS = 70;
const MAX_STAGGER_STEPS = 10;
const PINNED_THRESHOLD_PX = 80;
const ANCHOR_MARGIN_PX = 12;
const ANCHOR_SETTLE_MS = 4000;
const BOTTOM_ANCHOR = 'bottom';

type AnchorTarget = string | typeof BOTTOM_ANCHOR;

interface MessageListProps {
  messages: AssistantMessage[];
  uploadsById: Map<string, AssistantUploadView>;
  isRunning: boolean;
  isTyping: boolean;
  hasOlder: boolean;
  isLoadingOlder: boolean;
  onLoadOlder: () => void;
  onDecide: (interruptId: string, decisions: AssistantDecision[]) => void;
}

export function MessageList({
  messages,
  uploadsById,
  isRunning,
  isTyping,
  hasOlder,
  isLoadingOlder,
  onLoadOlder,
  onDecide,
}: MessageListProps) {
  const { t } = useTranslation('adminAssistant');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const hasAlignedRef = useRef(false);
  const lastMessageIdRef = useRef<string | null>(
    messages[messages.length - 1]?.id ?? null,
  );
  const firstMessageIdRef = useRef<string | null>(messages[0]?.id ?? null);
  const heightBeforeOlderRef = useRef<number | null>(null);
  const anchorRef = useRef<AnchorTarget | null>(null);
  const anchorTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const isPinnedRef = useRef(true);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [renderedCount, setRenderedCount] = useState(messages.length);
  const [batchStart, setBatchStart] = useState(0);

  if (messages.length !== renderedCount) {
    setBatchStart(messages.length > renderedCount ? renderedCount : 0);
    setRenderedCount(messages.length);
  }

  const scrollToBottom = (behavior: ScrollBehavior) => {
    const element = scrollRef.current;
    if (element) element.scrollTo({ top: element.scrollHeight, behavior });
  };

  const alignToMessage = (id: string, behavior: ScrollBehavior) => {
    const element = scrollRef.current;
    const node = element?.querySelector(
      `[data-message-id="${CSS.escape(id)}"]`,
    );
    if (!element || !node) return;
    const offset =
      node.getBoundingClientRect().top -
      element.getBoundingClientRect().top +
      element.scrollTop;
    element.scrollTo({ top: offset - ANCHOR_MARGIN_PX, behavior });
  };

  const alignTo = (target: AnchorTarget, behavior: ScrollBehavior) => {
    if (target === BOTTOM_ANCHOR) scrollToBottom(behavior);
    else alignToMessage(target, behavior);
  };

  const anchorTo = (target: AnchorTarget, behavior: ScrollBehavior) => {
    anchorRef.current = target;
    alignTo(target, behavior);
    clearTimeout(anchorTimerRef.current);
    anchorTimerRef.current = setTimeout(() => {
      anchorRef.current = null;
    }, ANCHOR_SETTLE_MS);
  };

  const releaseAnchor = () => {
    anchorRef.current = null;
    clearTimeout(anchorTimerRef.current);
  };

  useEffect(() => {
    if (messages.length === 0 || hasAlignedRef.current) return;
    hasAlignedRef.current = true;
    anchorTo(BOTTOM_ANCHOR, 'auto');
  }, [messages.length]);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    const previousFirst = firstMessageIdRef.current;
    firstMessageIdRef.current = messages[0]?.id ?? null;
    if (
      element &&
      heightBeforeOlderRef.current !== null &&
      previousFirst !== messages[0]?.id
    ) {
      element.scrollTop += element.scrollHeight - heightBeforeOlderRef.current;
      heightBeforeOlderRef.current = null;
    }
  }, [messages]);

  useEffect(() => {
    const previousLastId = lastMessageIdRef.current;
    const arrived = messages[messages.length - 1];
    const before = messages[messages.length - 2];
    lastMessageIdRef.current = arrived?.id ?? null;

    if (!hasAlignedRef.current || !arrived || arrived.id === previousLastId) {
      return;
    }

    const isOwn = arrived.role === AssistantMessageRole.USER;
    const opensTheReply =
      arrived.role === AssistantMessageRole.ASSISTANT &&
      before?.role === AssistantMessageRole.USER;

    if (isOwn || opensTheReply || isPinnedRef.current) {
      anchorTo(BOTTOM_ANCHOR, 'smooth');
    }
  }, [messages]);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    element.addEventListener('wheel', releaseAnchor, { passive: true });
    element.addEventListener('touchstart', releaseAnchor, { passive: true });
    return () => {
      element.removeEventListener('wheel', releaseAnchor);
      element.removeEventListener('touchstart', releaseAnchor);
      clearTimeout(anchorTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const element = scrollRef.current;
    const content = contentRef.current;
    if (!element || !content) return;

    const observer = new ResizeObserver(() => {
      if (anchorRef.current) {
        alignTo(anchorRef.current, 'auto');
      } else if (isPinnedRef.current) {
        scrollToBottom('auto');
      }
      const isNearBottom =
        element.scrollHeight - element.scrollTop - element.clientHeight <
        PINNED_THRESHOLD_PX;
      isPinnedRef.current = isNearBottom;
      setIsAtBottom(isNearBottom);
    });

    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  const loadOlder = () => {
    heightBeforeOlderRef.current = scrollRef.current?.scrollHeight ?? null;
    onLoadOlder();
  };

  return (
    <div
      ref={scrollRef}
      onScroll={(event) => {
        const element = event.currentTarget;
        const isNearBottom =
          element.scrollHeight - element.scrollTop - element.clientHeight <
          PINNED_THRESHOLD_PX;
        isPinnedRef.current = isNearBottom;
        setIsAtBottom(isNearBottom);
      }}
      className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-4"
    >
      <div
        ref={contentRef}
        className="flex min-h-full w-full flex-col justify-end gap-3"
      >
        {hasOlder && (
          <button
            type="button"
            onClick={loadOlder}
            disabled={isLoadingOlder}
            className="mx-auto flex items-center gap-1.5 rounded-full px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
          >
            {isLoadingOlder && <Loader2 className="h-3 w-3 animate-spin" />}
            {t('panel.loadOlder')}
          </button>
        )}
        <ul
          className="flex flex-col gap-3"
          aria-label={t('panel.title')}
          aria-live="polite"
          aria-relevant="additions"
        >
          {messages.map((message, index) => (
            <MessageBubble
              key={message.id}
              message={message}
              uploadsById={uploadsById}
              isRunning={isRunning}
              onDecide={onDecide}
              enterDelayMs={
                Math.min(Math.max(0, index - batchStart), MAX_STAGGER_STEPS) *
                STAGGER_MS
              }
            />
          ))}
        </ul>
        {isTyping ? <TypingIndicator /> : null}
      </div>

      <div className="pointer-events-none sticky bottom-0 -mr-1 -mt-9 flex translate-y-2 justify-end">
        <button
          type="button"
          onClick={() => scrollToBottom('smooth')}
          aria-hidden={isAtBottom}
          tabIndex={isAtBottom ? -1 : 0}
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-full border border-border/60 bg-card text-muted-foreground shadow-lg transition-[opacity,transform] duration-200 ease-out hover:text-foreground',
            isAtBottom
              ? 'translate-y-3 opacity-0'
              : 'pointer-events-auto translate-y-0 opacity-100',
          )}
        >
          <ArrowDown className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
